import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import { matchRoutes } from 'react-router-config';
import loadable from '@loadable/component';
import * as R from 'ramda';
import classNames from 'classnames';
import Container from 'reactstrap/lib/Container';
import { App as AppCore } from 'core/app';
import {
  withCoreComponent, withLocale, withUser, withModalActions, withImagesData
} from 'core/hocs';
import {
  wrapRoute2Locale, getEnv, isEmptyOrNil, convertStringifiedBoolean
} from 'core/helpers';
import { DEVICES_TYPES, USER_FIELDS } from 'core/constants';

import { withPwaInstall, withPwaInstallContext } from 'hocs/with-pwa-install';
import { IconsDefs } from 'components/icons/icons-defs';
import { Header } from 'components/header/header';
import { SupportModal } from 'components/modals/support-modal/support-modal';
import { LinkErrorModal } from 'components/modals/link-error-modal/link-error-modal';
import { DepositModal } from 'components/modals/deposit-modal/deposit-modal';
import { NewLevelModal } from 'components/modals/new-level-modal/new-level-modal';
import { CompleteSignUpModal } from 'components/modals/complete-sign-up-modal/complete-sign-up-modal';
import { ResetPasswordModal } from 'components/modals/reset-password-modal/reset-password-modal';
import { GlobalNotificationsStack } from 'components/global-notifications-stack/global-notifications-stack';
import { Sidebar } from 'components/sidebar/sidebar';
import { Footer } from 'components/footer/footer';
import { CasinoNavigation } from 'components/casino-navigation/casino-navigation';
// import { RecaptchaLoader } from 'components/recaptcha-loader/recaptcha-loader';
import { GlobalEventsHandler } from 'components/global-events-handler/global-events-handler';
import { SkipServerRender } from 'components/skip-server-render/skip-server-render';
import { LocationTracker } from 'components/location-tracker/location-tracker';
import { ConfirmOtpModal } from 'components/modals/confirm-otp-modal/confirm-otp-modal';
import { SetupOtpModal } from 'components/modals/setup-otp-modal/setup-otp-modal';
import { RemoveOtpModal } from 'components/modals/remove-otp-modal/remove-otp-modal';
import { CookiesPolicy } from 'components/cookies-policy/cookies-policy';
import { MobileGameContainer } from 'components/mobile-game-container/mobile-game-container';
import { PaymentContainerModal } from 'components/payment-container-modal/payment-container-modal';
import { PwaInstallGuide } from 'components/pwa-install-guide/pwa-install-guide';
import { SignInModal } from 'components/modals/sign-in-modal/sign-in-modal';
import { SignUpModal } from 'components/modals/sign-up-modal/sign-up-modal';
import { PromoInfoModal } from 'src/components/modals/promo-info-modal/promo-info-modal';
import { SocialAuthTracker } from 'components/social-auth-tracker/social-auth-tracker';
import { FreeSpinsNotifier } from 'components/free-spins-notifier/free-spins-notifier';
import { ConfirmPhoneModal } from 'components/modals/confirm-phone-modal/confirm-phone-modal';
import { PaymentCompletionModal } from 'components/modals/payment-completion-modal/payment-completion-modal';
import { SideModalBar } from 'components/side-modal-bar/side-modal-bar';
// import { RouteTracker } from 'components/route-tracker/route-tracker';
import { CioTracker } from 'components/trackers/cio-tracker';
import { CioFcmTracker } from 'components/trackers/cio-fcm-tracker';
import { CioFcmPrompt } from 'components/cio-fcm-prompt/cio-fcm-prompt';
import { DomainBar } from 'components/domain-bar/domain-bar';
import { DomainBarContext, withDomainBar } from 'hocs/with-domain-bar';
import { LiveChatIncChatLoader } from './components/live-chats/live-chat-inc-chat/live-chat-inc-chat';
import { updateManifestPerRoute } from './helpers/manifest';
import { setColorTheme, getColorTheme } from './helpers/ls';
import { ThemeModeContext } from './contexts/theme-mode-ctx/theme-mode-ctx';

import {
  PAGE_NAMES,
  LIVECHAT_INC_GROUPS,
  MODAL_IDS,
  IMAGES_DATA_CATEGORIES,
  THEME_MODE,
  LIGHT_THEME_CLASS,
  DARK_THEME_CLASS,
  STORAGE_THEME_COLOR,
} from './constants';

import './locales';

import './app.scss';

const { LOGGED_IN_MODAL_BANNER } = IMAGES_DATA_CATEGORIES;

const {
  LOYALTY,
  GAME,
  TOURNAMENT,
  RESET_PASSWORD,
  HOME,
  WALLET,
  TOURNAMENTS,
  GAMES,
  PROVIDER,
  CATEGORY,
  PAYMENTS,
  BANKING_HISTORY,
  SPORTSBOOK,
  IN_PLAY,
  FAQ,
  LOGIN_STANDALONE,
} = PAGE_NAMES;

const SWITCH_MODE = {
  true: THEME_MODE.LIGHT,
  false: THEME_MODE.DARK
};

const PageNotFound = loadable(() =>
  import(/* webpackChunkName: "page-not-found" */ './pages/page-not-found/page-not-found'));

const isStandaloneSignInActive = convertStringifiedBoolean(getEnv('STANDALONE_SIGN_IN'));

export class AppUI extends Component {
  static propTypes = {
    renderRoutes: PropTypes.func.isRequired,
    route: PropTypes.shape({
      routes: PropTypes.arrayOf(
        PropTypes.shape({
          path: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
          exact: PropTypes.bool,
        })
      ),
    }).isRequired,
    is404: PropTypes.bool.isRequired,
    deviceType: PropTypes.string.isRequired,
    history: PropTypes.shape().isRequired,
    userData: PropTypes.shape().isRequired,
    locale: PropTypes.string.isRequired,
    isPwa: PropTypes.bool.isRequired,
    openModal: PropTypes.func.isRequired,
    isUserLoggedIn: PropTypes.bool.isRequired,
    isUserRegistered: PropTypes.bool.isRequired,
    imagesData: PropTypes.shape().isRequired,
    currentModal: PropTypes.shape().isRequired,
    intl: intlShape.isRequired,
  };

  static contextType = withPwaInstallContext;

  state = {
    mode: getColorTheme() === THEME_MODE.LIGHT,
  };

  componentDidMount() {
    if (isStandaloneSignInActive) {
      updateManifestPerRoute(LOGIN_STANDALONE);
    }

    const storageTheme = getColorTheme();

    this.setState({ mode: storageTheme === THEME_MODE.LIGHT });

    document.body.classList.add(storageTheme === THEME_MODE.LIGHT ? LIGHT_THEME_CLASS : DARK_THEME_CLASS);
  }

  componentDidUpdate(prevProps, prevState) {
    const { isUserLoggedIn, openModal } = this.props;
    const { isUserLoggedIn: isUserLoggedInPrev } = prevProps;
    const { mode } = this.state;

    if (!isUserLoggedInPrev && isUserLoggedIn && this.isPromoBannerExist()) {
      openModal(MODAL_IDS.PROMO_INFO);
    }

    if (prevState.mode !== mode) {
      const prevBodyClass = prevState.mode ? LIGHT_THEME_CLASS : DARK_THEME_CLASS;
      document.body.classList.remove(prevBodyClass);

      document.cookie = `${STORAGE_THEME_COLOR}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; max-age=0`;

      const bodyClass = mode ? LIGHT_THEME_CLASS : DARK_THEME_CLASS;
      document.body.classList.add(bodyClass);

      document.cookie = `${STORAGE_THEME_COLOR}=${mode ? THEME_MODE.LIGHT : THEME_MODE.DARK}; path=/; max-age=600000000`;
    }
  }

  isPromoBannerExist = () => {
    const { imagesData } = this.props;

    return !isEmptyOrNil(imagesData[LOGGED_IN_MODAL_BANNER]);
  };

  switchMode = () => {
    this.setState(prevState => ({
      mode: !prevState.mode,
    }), () => (
      setColorTheme(SWITCH_MODE[this.state.mode])
    ));
  };

  render() {
    const { mode } = this.state;

    const {
      renderRoutes,
      route: { routes },
      is404,
      history,
      locale,
      deviceType,
      isPwa,
      userData,
      isUserLoggedIn,
      isUserRegistered,
      imagesData,
      currentModal: { id },
      intl: { formatMessage },
    } = this.props;

    // const { isPwaGuideReady, isPwaInstallClose } = this.context;

    const [routeInfo] = matchRoutes(routes, history.location.pathname);
    const currentRoute = R.propOr({}, 'route', routeInfo);
    const isDomainBarDisabledExternally = formatMessage({ id: 'domain.bar.disabled' }).trim() === 'true';

    const {
      page: pageName,
      noSidebar: hideSidebar,
      noHeader: hideHeader,
      // noChat: hideChat,
      noCookiesPopup: hideCookiesPopup,
      noFooter,
      hasNavigation,
      mobileBackRoute,
    } = currentRoute;

    const routeParams = R.pathOr({}, ['match', 'params'], routeInfo);
    const isLoyaltyPage = pageName === LOYALTY;
    const isGamePage = pageName === GAME;
    const isGamesPage = pageName === GAMES;
    const isHomePage = pageName === HOME;
    const isTournamentPage = pageName === TOURNAMENT;
    const isTournamentsPage = pageName === TOURNAMENTS;
    const isResetPasswordPage = pageName === RESET_PASSWORD;
    const isWalletPage = pageName === WALLET;
    const isProviderPage = pageName === PROVIDER;
    const isCategoryPage = pageName === CATEGORY;
    const isPaymentsPage = pageName === PAYMENTS;
    const isSportsbookPage = [SPORTSBOOK, IN_PLAY].includes(pageName);
    const isFaqPage = pageName === FAQ;
    const isBankingHistoryPage = pageName === BANKING_HISTORY;
    const isLoginPage = pageName === LOGIN_STANDALONE;
    const liveChatGroup = LIVECHAT_INC_GROUPS[locale];
    const isGameCategoriesModalOpened = MODAL_IDS.GAMES_CATEGORIES === id;

    if (isStandaloneSignInActive && !isUserLoggedIn && !isLoginPage) {
      history.push(wrapRoute2Locale('/login', locale));
    }

    const isMobile = deviceType === DEVICES_TYPES.MOBILE;
    const isGamePageOnMobile = isMobile && isGamePage;

    if (is404) {
      return <PageNotFound />;
    }

    return (
      <ThemeModeContext.Provider
        value={{
          themeMode: mode,
          switchMode: this.switchMode
        }}
      >
        <GlobalNotificationsStack />

        {/* for correct opening mobile game in iframe by direct link */}
        {/* <MobileGameContainer /> should mount before <Game /> */}
        {(isPwa || isMobile) && <MobileGameContainer isMobile={isMobile} isPwa={isPwa} isGamePage={isGamePage} />}

        <DomainBarContext.Consumer>
          {value => (
            <>
              {value.isDomainBarOpen && !isDomainBarDisabledExternally && <DomainBar className={classNames({ 'hide-domain-bar': isGameCategoriesModalOpened || isLoginPage })} />}

              <SkipServerRender skip>
                {!isPwa
                  && !value.isDomainBarOpen
                  && !isSportsbookPage
                  && !isGamePage
                  && !isLoginPage
                  && !isResetPasswordPage
                  && <PwaInstallGuide />}
              </SkipServerRender>

              <div className={classNames({ 'with-domain-bar': value.isDomainBarOpen && !isDomainBarDisabledExternally && !isGameCategoriesModalOpened && !isLoginPage })}>
                {!hideSidebar && !isGamePageOnMobile
                  && <Sidebar pageName={pageName} mobileBackRoute={mobileBackRoute} />}

                {!hideHeader && (
                  <Header
                    pageName={pageName}
                    mobileBackRoute={mobileBackRoute}
                    params={routeParams.category}
                    className={classNames({
                      'main-content-overlap': [HOME, GAMES].includes(pageName),
                      'position-absolute casino-header-overlay': isHomePage || isGamesPage,
                    })}
                  />
                )}

                <Container
                  fluid
                  className={classNames('d-flex flex-column main-container position-relative flex-grow-1', {
                    'is-main-page': !isLoyaltyPage && !isGamePage && !isHomePage && !isTournamentPage && !isGamesPage,
                    'px-sm-2_5 px-lg-5':
                      !isLoyaltyPage
                      && !isGamePage
                      && !isTournamentPage
                      && !isHomePage
                      && !isGamesPage
                      && !isWalletPage
                      && !isBankingHistoryPage
                      && !isSportsbookPage,
                    'px-0':
                      isLoyaltyPage
                      || isGamePage
                      || isTournamentPage
                      || isHomePage
                      || isWalletPage
                      || isGamesPage
                      || isSportsbookPage,
                    'hide-sidebar': hideSidebar || isGamePageOnMobile,
                    'has-navigation': hasNavigation && noFooter,
                    'has-content-under-header': isHomePage || isTournamentPage || isGamesPage,
                    'with-header': !hideHeader,
                    'px-sm-5': isWalletPage || isBankingHistoryPage,
                  })}
                >
                  {renderRoutes(routes)}
                </Container>
              </div>
            </>
          )}
        </DomainBarContext.Consumer>
        {!noFooter && (
          <Footer
            className={classNames({
              'mt-6':
                !isLoyaltyPage
                && !isTournamentsPage
                && !isGamesPage
                && !isCategoryPage
                && !isProviderPage
                && !isPaymentsPage
                && !isGamePage
                && !isFaqPage
                && !isSportsbookPage,
              'has-navigation': hasNavigation,
              'pt-3_5': isLoyaltyPage,
              'px-2 px-sm-2_5 px-lg-5': !isResetPasswordPage,
              'reset-password-page-footer mx-auto px-2_5': isResetPasswordPage,
              'hide-sidebar': hideSidebar,
              'mt-5': isProviderPage || isCategoryPage || isPaymentsPage,
            })}
          />
        )}

        <SkipServerRender skip>
          {/* {isStandaloneSignInActive && <RouteTracker isUserLoggedIn={isUserLoggedIn} />} */}
          <SocialAuthTracker />
          <SignInModal />
          <SignUpModal />
          <PromoInfoModal imagesData={imagesData} />
          <SupportModal />

          <SideModalBar pageName={pageName} params={routeParams.category} isSportsbookPage={isSportsbookPage} />

          <CompleteSignUpModal />
          <ResetPasswordModal />
          <LinkErrorModal />
          <DepositModal />
          <NewLevelModal />
          <FreeSpinsNotifier />
          <ConfirmPhoneModal />
          <PaymentCompletionModal />

          <ConfirmOtpModal />
          <SetupOtpModal />
          <RemoveOtpModal />

          {hasNavigation && <CasinoNavigation pageName={pageName} params={routeParams.category} />}

          <LiveChatIncChatLoader group={liveChatGroup} />
          <CioFcmPrompt userData={userData} isUserLoggedIn={isUserLoggedIn} />

          {/* <RecaptchaLoader /> */}

          <LocationTracker pageName={pageName} />

          {!hideCookiesPopup && !isPwa && <CookiesPolicy hasNavigation={hasNavigation} noSidebar={hideSidebar} />}
          <PaymentContainerModal />

          <CioTracker userData={userData} isUserLoggedIn={isUserLoggedIn} isUserRegistered={isUserRegistered} />
          <CioFcmTracker userData={userData} isUserLoggedIn={isUserLoggedIn} />
        </SkipServerRender>

        <GlobalEventsHandler pageName={pageName} />
        <IconsDefs />
      </ThemeModeContext.Provider>
    );
  }
}

export const App = withCoreComponent(
  AppCore,
  withLocale(
    withPwaInstall(
      withImagesData(
        withModalActions(
          withDomainBar(
            injectIntl(
              withUser(
                AppUI,
                [
                  USER_FIELDS.IS_USER_LOGGED_IN,
                  USER_FIELDS.IS_USER_REGISTERED,
                  USER_FIELDS.IS_USER_REGISTERED,
                  USER_FIELDS.USER_DATA,
                ]
              )
            )
          )
        )
      )
    )
  )
);
